/*  Blog listing — /blog
    ────────────────────────────────────────────────────────────────
    iter100.153 — Public blog index. Hero + audience toggle + featured
    post + category chip strip + search + cards grid.
*/
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Sparkle, MagnifyingGlass, ArrowRight, Clock, User, X,
    Heart, Stethoscope, Stack, Newspaper, ShieldCheck,
} from "@phosphor-icons/react";
import { useBlogList, useBlogCategories } from "@/lib/useBlog";
import SeoHead from "@/components/site/SeoHead";


const CATEGORY_ICONS = {
    "smile-science":      Sparkle,
    "patient-stories":    Heart,
    "clinical-insights":  Stethoscope,
    "product-deep-dives": Stack,
    "industry-news":      Newspaper,
    "aftercare":          ShieldCheck,
};


const AUDIENCE_TABS = [
    { id: null,            label: "All" },
    { id: "patient",       label: "For Patients" },
    { id: "professional",  label: "For Professionals" },
];


function fmtDate(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
    } catch { return ""; }
}


export default function Blog() {
    const [audience, setAudience] = useState(null);
    const [category, setCategory] = useState(null);
    const [search, setSearch] = useState("");
    const { categories } = useBlogCategories();
    const { items, total, loading } = useBlogList({ audience, category, search, page: 1, perPage: 24 });

    // Featured pin — first post with `featured: true`, else first item.
    const featured = useMemo(
        () => items.find((p) => p.featured) || items[0] || null,
        [items],
    );
    const rest = useMemo(
        () => items.filter((p) => p.id !== featured?.id),
        [items, featured],
    );

    return (
        <div data-testid="blog-page">
            <SeoHead
                page="blog"
                fallbackTitle="The Invisique™ Blog · Smile science, patient stories, and clinical insight"
                fallbackDescription="Honest writing from Invisique™ — clinical insight, patient stories, aftercare, and industry perspective on modern orthodontics."
            />

            <Hero
                search={search}
                onSearchChange={setSearch}
                audience={audience}
                onAudienceChange={setAudience}
                total={total}
            />

            <CategoryStrip
                categories={categories}
                activeId={category}
                onChange={setCategory}
                search={search}
            />

            <div className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
                {loading && items.length === 0 && (
                    <div className="py-20 text-center text-muted-foreground" data-testid="blog-loading">
                        Loading the library…
                    </div>
                )}

                {!loading && items.length === 0 && (
                    <EmptyState hasFilters={Boolean(audience || category || search)} onReset={() => { setAudience(null); setCategory(null); setSearch(""); }} />
                )}

                {featured && (
                    <FeaturedCard post={featured} />
                )}

                {rest.length > 0 && (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mt-12" data-testid="blog-grid">
                        {rest.map((post, i) => (
                            <PostCard key={post.id} post={post} idx={i} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}


/* ================================================================
   Hero
   ================================================================ */
function Hero({ search, onSearchChange, audience, onAudienceChange, total }) {
    return (
        <section className="relative overflow-hidden bg-background border-b border-border/60" data-testid="blog-hero">
            <div className="absolute inset-0 blue-grid-bg opacity-50 mask-fade-b pointer-events-none" aria-hidden="true" />
            <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 lg:pt-20 pb-10 relative">
                <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full bg-white border border-border/70 shadow-sm mb-6">
                    <Sparkle size={13} weight="fill" className="text-amber-500" />
                    <span className="text-xs font-semibold text-foreground">
                        The Invisique™ library <span className="text-muted-foreground font-normal">· {total} article{total === 1 ? "" : "s"}</span>
                    </span>
                </div>

                <div className="overline text-primary mb-4">Perspective, honestly written</div>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-[3.5rem] font-bold tracking-tight leading-[1.05]">
                    Smile science,{" "}
                    <span className="text-primary">patient stories,</span>
                    <br />
                    and clinical insight.
                </h1>
                <p className="mt-6 text-lg text-muted-foreground max-w-2xl">
                    Written by certified orthodontists, patient success leads, and our Centre-of-Excellence team. Read the whole library or filter to what matters to you.
                </p>

                <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-center">
                    {/* Search */}
                    <div className="relative flex-1 max-w-lg">
                        <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
                            <MagnifyingGlass size={18} weight="duotone" className="text-primary" />
                        </div>
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => onSearchChange(e.target.value)}
                            placeholder="Search articles…"
                            className="w-full h-12 pl-11 pr-11 rounded-full border-2 border-border bg-white shadow-sm text-sm font-medium focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                            data-testid="blog-search"
                        />
                        {search && (
                            <button
                                type="button"
                                onClick={() => onSearchChange("")}
                                className="absolute inset-y-0 right-3 flex items-center text-muted-foreground hover:text-foreground"
                                aria-label="Clear search"
                            >
                                <X size={18} weight="bold" />
                            </button>
                        )}
                    </div>

                    {/* Audience tabs */}
                    <div className="inline-flex bg-secondary/70 rounded-full p-1" data-testid="blog-audience-tabs">
                        {AUDIENCE_TABS.map((t) => {
                            const active = audience === t.id;
                            return (
                                <button
                                    key={String(t.id)}
                                    type="button"
                                    onClick={() => onAudienceChange(t.id)}
                                    className={`h-10 px-4 rounded-full text-xs font-bold tracking-wide uppercase transition-all ${
                                        active
                                            ? "bg-primary text-primary-foreground shadow-sm"
                                            : "text-foreground/60 hover:text-foreground"
                                    }`}
                                    data-testid={`blog-audience-${t.id || "all"}`}
                                    aria-pressed={active}
                                >
                                    {t.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
}


/* ================================================================
   Category chip strip
   ================================================================ */
function CategoryStrip({ categories, activeId, onChange, search }) {
    if (!categories.length) return null;
    return (
        <section className="sticky top-16 z-30 bg-background/95 backdrop-blur-md border-b border-border/60">
            <div className="max-w-7xl mx-auto px-5 sm:px-8 py-3 overflow-x-auto">
                <div className="flex items-center gap-2 min-w-max" data-testid="blog-category-strip">
                    <CategoryChip label="All" active={!activeId && !search} onClick={() => onChange(null)} testId="blog-category-all" />
                    {categories.map((c) => {
                        const Icon = CATEGORY_ICONS[c.id] || Sparkle;
                        return (
                            <CategoryChip
                                key={c.id}
                                icon={Icon}
                                label={c.label}
                                count={c.count}
                                active={activeId === c.id}
                                onClick={() => onChange(activeId === c.id ? null : c.id)}
                                testId={`blog-category-${c.id}`}
                            />
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

function CategoryChip({ icon: Icon, label, count, active, onClick, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            aria-pressed={active}
            className={`inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-xs font-semibold tracking-wide whitespace-nowrap transition-all ${
                active
                    ? "bg-primary text-primary-foreground shadow-sm ring-2 ring-primary/20"
                    : "bg-secondary/70 text-foreground/75 hover:bg-secondary hover:text-foreground"
            }`}
        >
            {Icon ? <Icon size={13} weight="fill" /> : null}
            <span>{label}</span>
            {typeof count === "number" && count > 0 && (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${active ? "bg-white/25" : "bg-white/70"}`}>
                    {count}
                </span>
            )}
        </button>
    );
}


/* ================================================================
   Featured card (large, editorial)
   ================================================================ */
function FeaturedCard({ post }) {
    const Icon = CATEGORY_ICONS[post.category] || Sparkle;
    return (
        <motion.article
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mt-10 lg:mt-14"
            data-testid="blog-featured-card"
        >
            <Link
                to={`/blog/${post.slug}`}
                className="group grid lg:grid-cols-2 gap-6 lg:gap-10 items-center rounded-3xl overflow-hidden hover:bento-shadow transition-all"
            >
                <div className="relative aspect-[16/11] lg:aspect-[4/3] rounded-2xl overflow-hidden bg-secondary">
                    {post.hero_image_url ? (
                        <img
                            src={post.hero_image_url}
                            alt={post.hero_image_alt || post.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                            loading="lazy"
                        />
                    ) : (
                        <div className="w-full h-full grid place-items-center">
                            <Sparkle size={48} weight="duotone" className="text-primary/30" />
                        </div>
                    )}
                    <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/95 backdrop-blur-md shadow-lg">
                        <Sparkle size={11} weight="fill" className="text-amber-500" />
                        <span className="text-[10px] font-bold tracking-[0.16em] uppercase text-foreground">Featured</span>
                    </div>
                </div>
                <div>
                    <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-primary mb-3">
                        <Icon size={12} weight="fill" />
                        {categoryLabel(post.category)}
                    </div>
                    <h2 className="font-display text-2xl sm:text-3xl lg:text-[2.25rem] font-bold tracking-tight leading-tight text-foreground group-hover:text-primary transition-colors">
                        {post.title}
                    </h2>
                    <p className="mt-4 text-base text-muted-foreground leading-relaxed line-clamp-3">
                        {post.excerpt}
                    </p>

                    <div className="mt-6 flex items-center gap-4 text-xs text-muted-foreground">
                        <MetaChip icon={User} label="Invisique™ Editorial" />
                        <MetaChip icon={Clock} label={`${post.reading_time_min || 1} min read`} />
                        <span className="hidden sm:inline">· {fmtDate(post.published_at)}</span>
                    </div>

                    <div className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-primary group-hover:gap-2 transition-all">
                        Read article
                        <ArrowRight size={14} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
                    </div>
                </div>
            </Link>
        </motion.article>
    );
}


/* ================================================================
   Post card (grid)
   ================================================================ */
function PostCard({ post, idx }) {
    const Icon = CATEGORY_ICONS[post.category] || Sparkle;
    return (
        <motion.article
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.4, delay: (idx % 6) * 0.04 }}
            data-testid={`blog-card-${post.slug}`}
        >
            <Link
                to={`/blog/${post.slug}`}
                className="group block rounded-2xl overflow-hidden border border-border/70 bg-card hover:border-primary/30 transition-all bento-shadow"
            >
                <div className="relative aspect-[4/3] bg-secondary overflow-hidden">
                    {post.hero_image_url ? (
                        <img
                            src={post.hero_image_url}
                            alt={post.hero_image_alt || post.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                        />
                    ) : (
                        <div className="w-full h-full grid place-items-center">
                            <Icon size={40} weight="duotone" className="text-primary/30" />
                        </div>
                    )}
                </div>
                <div className="p-5">
                    <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-primary mb-3">
                        <Icon size={11} weight="fill" />
                        {categoryLabel(post.category)}
                    </div>
                    <h3 className="font-display text-lg font-bold leading-snug text-foreground group-hover:text-primary transition-colors line-clamp-2">
                        {post.title}
                    </h3>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed line-clamp-2">
                        {post.excerpt}
                    </p>
                    <div className="mt-4 flex items-center gap-3 text-[11px] text-muted-foreground pt-4 border-t border-border/60">
                        <span className="font-medium text-foreground/80">Invisique&trade; Editorial</span>
                        <span>·</span>
                        <span>{post.reading_time_min || 1} min</span>
                        <span>·</span>
                        <span>{fmtDate(post.published_at)}</span>
                    </div>
                </div>
            </Link>
        </motion.article>
    );
}


function MetaChip({ icon: Icon, label }) {
    return (
        <div className="inline-flex items-center gap-1.5">
            <Icon size={12} weight="fill" className="text-primary" />
            <span className="font-medium text-foreground/70">{label}</span>
        </div>
    );
}


function categoryLabel(id) {
    const map = {
        "smile-science": "Smile Science",
        "patient-stories": "Patient Stories",
        "clinical-insights": "Clinical Insights",
        "product-deep-dives": "Product Deep-Dives",
        "industry-news": "Industry & News",
        "aftercare": "Aftercare",
    };
    return map[id] || id;
}


function EmptyState({ hasFilters, onReset }) {
    return (
        <div className="py-24 text-center max-w-md mx-auto" data-testid="blog-empty">
            <div className="w-16 h-16 rounded-full bg-secondary grid place-items-center mx-auto mb-4">
                <Sparkle size={22} weight="duotone" className="text-primary/60" />
            </div>
            <h2 className="font-display text-xl font-bold">
                {hasFilters ? "No articles match those filters yet." : "No articles published yet."}
            </h2>
            <p className="text-muted-foreground text-sm mt-2">
                {hasFilters
                    ? "Try clearing filters or switching audience."
                    : "Check back soon — new perspective every month."}
            </p>
            {hasFilters && (
                <button
                    type="button"
                    onClick={onReset}
                    className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
                    data-testid="blog-empty-reset"
                >
                    Clear all filters
                    <ArrowRight size={13} weight="bold" />
                </button>
            )}
        </div>
    );
}
